/* eslint-disable react-hooks/exhaustive-deps */
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/List";
import LogoutIcon from "@mui/icons-material/Logout";
import { IconButton, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { logout } from "../../app/slices/authSlice";
import { fetchLangResources } from "../../app/slices/languageSlice";
import { hasAccess, translate } from "../../app/utils";
import coa from "../../assets/images/coat_of_arms.png";
import enIcon from "../../assets/images/icon_en.png";
import swIcon from "../../assets/images/icon_sw.png";
import logo from "../../assets/images/logo.png";
import "./navigation.css";

const Navigation = () => {
  const { activeSession } = useSelector(({ auth }) => auth);
  const [isVisible, setIsVisible] = useState(false);
  const [isOpenLanguage, setIsOpenLanguage] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem("currentLanguage") || "en",
  );

  const languageDropdownRef = useRef(null);
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target)
      ) {
        setIsOpenLanguage(false);
      }

      // Close main dropdowns if clicking outside any dropdown
      if (!event.target.closest('.dropdown')) {
        setActiveDropdown(null);
        localStorage.removeItem('activeDropdown');
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (
      (isOpenLanguage && currentLanguage) ||
      localStorage.getItem("currentLanguage") === null
    ) {
      localStorage.setItem("currentLanguage", currentLanguage);
      setIsOpenLanguage(false);
      window.location.reload();
    }
  }, [currentLanguage]);

  useEffect(() => {
    const storedDropdown = localStorage.getItem("activeDropdown");
    if (storedDropdown) {
      setActiveDropdown(storedDropdown);
    }
  }, []);

  //* methods
  const toggleMenu = () => setIsVisible((prev) => !prev);
  const toggleLanguageDropdown = () => setIsOpenLanguage((prev) => !prev);
  const handleLanguageChange = (lang) => {
    if (lang !== currentLanguage) {
      dispatch(fetchLangResources());
      setCurrentLanguage(lang);
    } else {
      setIsOpenLanguage(false);
    }
  };

  const isActiveLink = (path) => location.pathname === path;

  const handleLinkClick = (path, dropdownName) => {
    setActiveDropdown(dropdownName || null); // Unset active dropdown if no dropdown
    localStorage.setItem("activeDropdown", dropdownName || "");
  };

  const handleDropdownClick = (dropdownName) => {
    if (activeDropdown === dropdownName) {
      setActiveDropdown(null);
      localStorage.removeItem("activeDropdown");
    } else {
      setActiveDropdown(dropdownName);
      localStorage.setItem("activeDropdown", dropdownName);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  return (
    <>
      <div className="app-navbar">
        {/* Banner */}
        <div className="nav-banner">
          <div className="left">
            <img src={coa} alt="Coat of arms" />
          </div>
          <div className="middle">BANK OF TANZANIA eLIBRARY</div>
          <div className="right">
            <img src={logo} alt="Bank of Tanzania" />
          </div>
        </div>

        {/* content */}
        <div className="nav-content">
          <div className="flex-row align-items-center">
            <div
              className={`navbar-links-container ${
                isVisible ? "navbar-visible" : "navbar-invisible"
              }`}
            >
              <a
                href="/"
                className={isActiveLink("/") ? "nav-active" : ""}
                onClick={() => handleLinkClick("/", null)}
              >
                {translate("home")}
              </a>
              <a
                href="/aboutUs"
                className={isActiveLink("/aboutUs") ? "nav-active" : ""}
                onClick={() => handleLinkClick("/aboutUs", null)}
              >
                {translate("aboutUs")}
              </a>
              {/* <a
                href="/contactUs"
                className={isActiveLink("/contactUs") ? "nav-active" : ""}
                onClick={() => handleLinkClick("/contactUs", null)} 
              >
                {translate("contactUs")}
              </a> */}
              <a
                href="/repository"
                className={isActiveLink("/repository") ? "nav-active" : ""}
                onClick={() => handleLinkClick("/repository", null)}
              >
                {translate("repository")}
              </a>

              <a
                href="/books"
                className={isActiveLink("/books") ? "nav-active" : ""}
              >
                {translate("bookCollection")}
              </a>

              {/* Resources Dropdown */}
              <div className={`dropdown`}>
                <button
                  className="dropdown-btn"
                  onClick={() => handleDropdownClick("resources")}
                >
                  {translate("e-resources")}
                  <div
                    className={`${
                      activeDropdown === "resources" ? "menu-active" : ""
                    }`}
                    style={{
                      fontSize: "0.7rem",
                      marginLeft: "5px",
                    }}
                  >
                    &#x25BC;
                  </div>
                </button>
                <div className={`dropdown-content ${activeDropdown === "resources" ? "show-dropdown" : ""}`}>
                  <a
                    href="/openSource"
                    className={isActiveLink("/openSource") ? "nav-active" : ""}
                    onClick={() => handleDropdownClick("resources")}
                  >
                    {translate("openSource")}
                  </a>
                  <a
                    href="http://liberty.bot.go.tz:8080"
                    className={isActiveLink("/e-resources") ? "nav-active" : ""}
                    onClick={() => handleDropdownClick("resources")}
                  >
                    {translate("subscribedDatabase")}
                  </a>
                </div>
              </div>

              {/* Administration Dropdown */}
              {hasAccess({ type: "name", resource: "VIEW ADMINISTRATION" }) && (
                <div className={`dropdown`}>
                  <button
                    className="dropdown-btn"
                    onClick={() => handleDropdownClick("admin")}
                  >
                    {translate("administration")}
                    <div
                      className={`dropdown ${
                        activeDropdown === "admin" ? "menu-active" : ""
                      }`}
                      style={{ fontSize: "0.7rem", marginLeft: "5px" }}
                    >
                      &#x25BC;
                    </div>
                  </button>
                  <div className={`dropdown-content ${activeDropdown === "admin" ? "show-dropdown" : ""}`}>
                    {hasAccess({ type: "path", resource: "/users" }) && (
                      <a
                        href="/users"
                        className={isActiveLink("/users") ? "nav-active" : ""}
                        onClick={() => handleDropdownClick("admin")}
                      >
                        {translate("users")}
                      </a>
                    )}

                    {hasAccess({ type: "path", resource: "/uploads" }) && (
                      <a
                        href="/uploads"
                        className={isActiveLink("/uploads") ? "nav-active" : ""}
                        onClick={() => handleDropdownClick("admin")}
                      >
                        {translate("uploads")}
                      </a>
                    )}

                    {hasAccess({ type: "path", resource: "/common" }) && (
                      <a
                        href="/common"
                        className={isActiveLink("/common") ? "nav-active" : ""}
                        onClick={() => handleDropdownClick("admin")}
                      >
                        {translate("common")}
                      </a>
                    )}

                    {hasAccess({ type: "path", resource: "/languages" }) && (
                      <a
                        href="/languages"
                        className={
                          isActiveLink("/languages") ? "nav-active" : ""
                        }
                        onClick={() => handleDropdownClick("admin")}
                      >
                        {translate("languages")}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div
            className="navbar-right"
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            <div
              className="container-lang"
              ref={languageDropdownRef}
              style={{ position: "relative" }}
            >
              <Tooltip title={translate("language")} placement="right">
                <button
                  className="button-lang"
                  onClick={toggleLanguageDropdown}
                  aria-label={translate("language")}
                >
                  <img
                    src={currentLanguage === "sw" ? swIcon : enIcon}
                    alt={currentLanguage === "sw" ? "Swahili" : "English"}
                  />
                </button>
              </Tooltip>

              {isOpenLanguage && (
                <div className="options-lang">
                  <div
                    className="item"
                    onClick={() => handleLanguageChange("en")}
                  >
                    <img src={enIcon} alt="English" /> English
                  </div>
                  <div
                    className="item"
                    onClick={() => handleLanguageChange("sw")}
                  >
                    <img src={swIcon} alt="Swahili" /> Swahili
                  </div>
                </div>
              )}
            </div>

            {activeSession && (
              <IconButton
                onClick={handleLogout}
                style={{
                  // backgroundColor: "transparent",
                  cursor: "pointer",
                }}
                aria-label={translate("logout")}
                title={translate("logout")}
              >
                <LogoutIcon />
              </IconButton>
            )}

            <div className="navbar-menu-icon" onClick={toggleMenu}>
              {isVisible ? <CloseIcon /> : <MenuIcon />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
